import { queryOptions, useQuery, type QueryClient } from '@tanstack/react-query';
import type { Me, PlattformRecht } from '@dxl/shared';
import { api, setzeCsrf } from './api';

export const meAbfrage = queryOptions({
  queryKey: ['me'],
  queryFn: async (): Promise<Me | null> => {
    const me = await api<Me | null>('/api/auth/me');
    setzeCsrf(me?.csrf ?? '');
    return me;
  },
  staleTime: 5 * 60_000,
});

export const einrichtungAbfrage = queryOptions({
  queryKey: ['einrichtung'],
  queryFn: () => api<{ offen: boolean }>('/api/einrichtung'),
  staleTime: Infinity,
});

export function useMe(): Me {
  const { data } = useQuery(meAbfrage);
  if (!data) throw new Error('useMe außerhalb des angemeldeten Bereichs');
  return data;
}

/** Sitzung nach Anmeldung/2FA sicher neu laden – auch wenn gerade niemand die Abfrage beobachtet */
export function meNeuLaden(qc: QueryClient) {
  return qc.fetchQuery({ ...meAbfrage, staleTime: 0 });
}

/** Nach dem Abmelden: Sitzung leeren, alle Daten des Kontos aus dem Cache entfernen */
export function sitzungLeeren(qc: QueryClient) {
  qc.setQueryData(meAbfrage.queryKey, null);
  qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' && q.queryKey[0] !== 'einrichtung' });
}

/** Kontext wechseln (DXL-Ansicht an/aus): Daten des bisherigen Kontos verwerfen, Sitzung neu laden */
export async function kontextWechseln(qc: QueryClient) {
  qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' && q.queryKey[0] !== 'einrichtung' });
  return meNeuLaden(qc);
}

/** Recht der DXL-Plattform aus dem Rechte-Level (Server prüft dasselbe – hier nur zum Ausblenden) */
export function darfPlattform(me: Me, recht: PlattformRecht): boolean {
  return me.nutzer.rolle === 'dxl_admin' && !!me.plattform?.rechte.includes(recht);
}
